import React from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import { VseHtmlField } from '../VseHtml/VseHtmlField';
import { VseTinymceEditorDialog, VseTinymceEditorDialogProps } from '../VseHtml/VseTinymceEditorDialog';
import { VseCourseEditTemplate } from '../types/VseCourseEditTemplate';
import {
  CourseStatusEntity,
  LanguageEntity,
} from '../types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  numberItem: {
    textAlign: 'right',
  },
}));

export type CourseErrorKeyResolver = (key: keyof VseCourseEditTemplate) => string;

export interface VseCourseEditGridProps {
  data: VseCourseEditTemplate,
  decodeSeason: (id: number) => string | undefined,
  decodeUniversity: (id: number) => string | undefined,
  decodeUniversityTerm: (seasonID: number, universityID: number, termCode: string) => string | undefined,
  courseStatusOptions: CourseStatusEntity[],
  languageOptions: LanguageEntity[],
  onChange: (changes: Partial<VseCourseEditTemplate>) => void,
  resolveCourseErrorKey: CourseErrorKeyResolver,
  valProblemKeys?: Array<string>,
}

export function VseCourseEditGrid(props: VseCourseEditGridProps) {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dateTimeFormatOptions = {
    year: 'numeric', month: '2-digit', day: '2-digit'
  };
  const firstDayOfClass = props.data.firstDayOfClass ? (new Intl.DateTimeFormat('en-CA', dateTimeFormatOptions)).format(new Date(props.data.firstDayOfClass)).replace(',', '') : null;
  const lastDayOfClass = props.data.lastDayOfClass ? (new Intl.DateTimeFormat('en-CA', dateTimeFormatOptions)).format(new Date(props.data.lastDayOfClass)).replace(',', '') : null;
  const units = parseFloat(props.data.units).toFixed(2);
  const teachHrsPerUnit = parseFloat(props.data.teachHrsPerUnit).toFixed(2);
  const coursePlaces = parseInt(props.data.coursePlaces).toString();

  const [editorProps, setEditorProps] = React.useState<VseTinymceEditorDialogProps>({
    open: false,
  });

  function hasError(key: keyof VseCourseEditTemplate): boolean {
    if (props.valProblemKeys) {
      const searchElement = props.resolveCourseErrorKey(key);
      return props.valProblemKeys.includes(searchElement);
    }
    return false;
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid container>
          <Grid item xs={12} md={3}>
            <TextField
              label='VSE Season'
              required
              value={props.decodeSeason(props.data.seasonID)}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label='Course Offering University'
              required
              value={props.decodeUniversity(props.data.univID)}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label='Course Offering Term / Semester'
              required
              value={props.decodeUniversityTerm(props.data.seasonID, props.data.univID, props.data.termCode)}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={3}>
            <TextField
              label='Course Code'
              required
              value={props.data.courseCode}
              onChange={(e) => props.onChange({ courseCode: e.target.value })}
              error={hasError('courseCode')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label='Course Title'
              required
              value={props.data.courseTitle}
              onChange={(e) => props.onChange({ courseTitle: e.target.value })}
              error={hasError('courseTitle')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label='Status'
              required
              select
              value={props.data.statusCode}
              onChange={(e) => props.onChange({ statusCode: e.target.value })}
              error={hasError('statusCode')}
            >
              {props.courseStatusOptions.map((option) => (
                <MenuItem
                  key={option.code}
                  value={option.code}>
                  {option.description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={6}>
            <TextField
              label='Course Offering Faculty/School/Department'
              required
              value={props.data.offerUnit}
              onChange={(e) => props.onChange({ offerUnit: e.target.value })}
              error={hasError('offerUnit')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label='Name of Course Teacher'
              required
              value={props.data.courseTeacher}
              onChange={(e) => props.onChange({ courseTeacher: e.target.value })}
              error={hasError('courseTeacher')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="link"
              label='Link to Teacher Profile'
              value={props.data.teacherProfile}
              onUpdated={(content) => {
                props.onChange({ teacherProfile: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('teacherProfile')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={2}>
            <TextField
              type='number'
              label='No. of Credits / Units'
              required
              inputProps={
                {
                  className: classes.numberItem,
                  step: 0.5,
                  min: 0
                }
              }
              value={units}
              onChange={(e) => props.onChange({ units: e.target.value })}
              error={hasError('units')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              type='number'
              label='No. of Teaching Hours'
              helperText='per Credit / Unit'
              required
              inputProps={
                {
                  className: classes.numberItem,
                  step: 0.5,
                  min: 0
                }
              }
              value={teachHrsPerUnit}
              onChange={(e) => props.onChange({ teachHrsPerUnit: e.target.value })}
              error={hasError('teachHrsPerUnit')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              type='number'
              label='Course Places for VSE'
              helperText='Min: 5 for each course; Max: 70 for each university'
              required
              inputProps={
                {
                  className: classes.numberItem,
                  min: 0
                }
              }
              value={coursePlaces}
              onChange={(e) => props.onChange({ coursePlaces: e.target.value })}
              error={hasError('coursePlaces')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label='Language of Instruction'
              required
              select
              value={props.data.language}
              onChange={(e) => props.onChange({ language: e.target.value })}
              error={hasError('language')}
            >
              {props.languageOptions.map((option) => (
                <MenuItem
                  key={option.code}
                  value={option.code}>
                  {option.description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="First Day of Class"
              type="date"
              required
              value={firstDayOfClass}
              onChange={(e) => props.onChange({ firstDayOfClass: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={hasError('firstDayOfClass')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Last Day of Class"
              type="date"
              required
              value={lastDayOfClass}
              onChange={(e) => props.onChange({ lastDayOfClass: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={hasError('lastDayOfClass')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="link"
              label='Link to Course Syllabus / Outline'
              value={props.data.courseLink}
              onUpdated={(content) => {
                props.onChange({ courseLink: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('courseLink')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Course Description'
              multiline
              rows={10}
              value={props.data.courseDescription}
              onUpdated={(content) => {
                props.onChange({ courseDescription: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('courseDescription')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Course Prerequisites/Restrictions'
              multiline
              rows={4}
              value={props.data.courseRestriction}
              onUpdated={(content) => {
                props.onChange({ courseRestriction: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('courseRestriction')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Points to Note for Students'
              multiline
              rows={4}
              value={props.data.pointNotes}
              onUpdated={(content) => {
                props.onChange({ pointNotes: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('pointNotes')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Fees to be Borne by Students'
              multiline
              rows={2}
              value={props.data.feeByStudents}
              onUpdated={(content) => {
                props.onChange({ feeByStudents: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('feeByStudents')}
            />
          </Grid>
        </Grid>
      </Grid>
      <VseTinymceEditorDialog {...editorProps} onClose={() => setEditorProps({ open: false })} />
    </div>
  );
}
